// Call lifecycle — the WhatsApp-style Ringing → Connecting → Active flow, derived
// from the native per-peer iceConnectionState. Pure and framework-agnostic so the
// exact same mapping drives web, mobile and CTV UIs (and is unit-testable in Node).
//
// Design note (from the product brief): do NOT decide call status purely on "is media
// flowing". Monitor iceConnectionState on each RTCPeerConnection and fold those into a
// single call-level phase the UI can render.

/** The native RTCPeerConnection.iceConnectionState values we care about. */
export type IceConnectionState =
  | 'new'
  | 'checking'
  | 'connected'
  | 'completed'
  | 'disconnected'
  | 'failed'
  | 'closed';

/** A single peer's connection reduced to a coarse, UI-friendly phase. */
export type PeerPhase = 'connecting' | 'active' | 'reconnecting' | 'closed';

/** The whole call's lifecycle phase (the WhatsApp flow). */
export type CallPhase = 'idle' | 'ringing' | 'connecting' | 'active' | 'reconnecting' | 'ended';

/** Why a call ended — drives the post-call UI copy. */
export type CallEndReason =
  | 'hangup' // we hung up
  | 'remote-hangup' // the other side hung up
  | 'declined' // callee rejected the invite
  | 'cancelled' // caller cancelled before pickup
  | 'no-answer' // ring timed out
  | 'failed'; // connection could not be (re)established

/** Map one peer's raw iceConnectionState onto a coarse UI phase. */
export function icePhase(state: IceConnectionState): PeerPhase {
  switch (state) {
    case 'connected':
    case 'completed':
      return 'active';
    case 'disconnected':
    case 'failed':
      return 'reconnecting';
    case 'closed':
      return 'closed';
    case 'new':
    case 'checking':
    default:
      return 'connecting';
  }
}

export interface CallPhaseInput {
  /** An invite handshake is outstanding (outgoing ring or incoming, not yet accepted). */
  invited: boolean;
  /** Both sides committed (we accepted an incoming call, or our invite was accepted). */
  accepted: boolean;
  /** The call has been explicitly ended. */
  ended: boolean;
  /** Live iceConnectionState of every active peer connection in the call. */
  peerIceStates: IceConnectionState[];
}

/**
 * Reduce the call to a single phase.
 *
 * Precedence: `ended` wins; before acceptance we are `ringing` (if an invite is
 * outstanding) or `idle`; after acceptance the phase is derived from the peers. A peer
 * connection that has begun negotiating implies the call is effectively accepted even
 * if the explicit accept signal was lost or reordered.
 *
 * Once any peer is live the call is `active` (WhatsApp shows a 1:1 as connected the
 * moment media flows; a group is active as soon as you are connected to at least one
 * peer). If no peer is live yet but one is recovering we surface `reconnecting`;
 * otherwise `connecting`.
 */
export function deriveCallPhase(input: CallPhaseInput): CallPhase {
  if (input.ended) return 'ended';

  const livePeers = input.peerIceStates.filter((s) => s !== 'closed');
  const negotiating = livePeers.length > 0;
  const accepted = input.accepted || negotiating;

  if (!accepted) return input.invited ? 'ringing' : 'idle';

  const phases = livePeers.map(icePhase);
  if (phases.some((p) => p === 'active')) return 'active';
  if (phases.some((p) => p === 'reconnecting')) return 'reconnecting';
  return 'connecting';
}
